import { lazy } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { AppShell } from "./components/AppShell";
import { AuthProvider, useAuth } from "./providers/AuthProvider";
import { DemoModeProvider } from "./providers/DemoModeProvider";
import { WorkspaceProvider } from "./providers/WorkspaceProvider";

const DashboardPage = lazy(() => import("./pages/DashboardPage").then((module) => ({ default: module.DashboardPage })));
const CasesPage = lazy(() => import("./pages/CasesPage").then((module) => ({ default: module.CasesPage })));
const AddCasePage = lazy(() => import("./pages/AddCasePage").then((module) => ({ default: module.AddCasePage })));
const AddListPage = lazy(() => import("./pages/AddListPage").then((module) => ({ default: module.AddListPage })));
const OneClickPage = lazy(() => import("./pages/OneClickPage").then((module) => ({ default: module.OneClickPage })));
const ImportPage = lazy(() => import("./pages/ImportPage").then((module) => ({ default: module.ImportPage })));
const ReportsPage = lazy(() => import("./pages/ReportsPage").then((module) => ({ default: module.ReportsPage })));
const ReportWorkspacePage = lazy(() => import("./pages/ReportWorkspacePage").then((module) => ({ default: module.ReportWorkspacePage })));
const ProgressPage = lazy(() => import("./pages/ProgressPage").then((module) => ({ default: module.ProgressPage })));
const DataQualityPage = lazy(() => import("./pages/DataQualityPage").then((module) => ({ default: module.DataQualityPage })));

export function App({ demoMode = false }: { demoMode?: boolean }) {
  return (
    <DemoModeProvider enabled={demoMode}>
      <AuthProvider disabled={demoMode}>
        <WorkspaceProvider demoMode={demoMode}>
          <StudioRoutes demoMode={demoMode} />
        </WorkspaceProvider>
      </AuthProvider>
    </DemoModeProvider>
  );
}

function StudioRoutes({ demoMode }: { demoMode: boolean }) {
  const { user, entitlement, loading } = useAuth();
  const accessPending = !demoMode && loading;
  const canReadLiveWorkspace = demoMode || accessPending || Boolean(user && entitlement !== "free");
  const canWriteLiveWorkspace = accessPending || (!demoMode && Boolean(user && entitlement === "pro"));
  const canPreviewImport = demoMode || canWriteLiveWorkspace;

  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route index element={<DashboardPage />} />
        <Route path="cases" element={canReadLiveWorkspace ? <CasesPage /> : <Navigate replace to="/" />} />
        <Route path="add" element={canWriteLiveWorkspace ? <AddCasePage /> : <Navigate replace to="/" />} />
        <Route path="add/list" element={canWriteLiveWorkspace ? <AddListPage /> : <Navigate replace to="/" />} />
        <Route path="one-click" element={canWriteLiveWorkspace ? <OneClickPage /> : <Navigate replace to="/" />} />
        <Route path="import" element={canPreviewImport ? <ImportPage /> : <Navigate replace to="/" />} />
        <Route path="reports" element={canReadLiveWorkspace ? <ReportsPage /> : <Navigate replace to="/" />} />
        <Route path="reports/:reportId" element={canWriteLiveWorkspace ? <ReportWorkspacePage /> : <Navigate replace to={canReadLiveWorkspace ? "/reports" : "/"} />} />
        <Route path="progress" element={canReadLiveWorkspace ? <ProgressPage /> : <Navigate replace to="/" />} />
        <Route path="data-quality" element={canReadLiveWorkspace ? <DataQualityPage /> : <Navigate replace to="/" />} />
        <Route path="*" element={<Navigate replace to="/" />} />
      </Route>
    </Routes>
  );
}
